// src/utils/useJobReminders.js
// Loads what the job reminders need beyond jobs + users (dismissals, recent
// clock-ins, the sub contractor list) and builds the reminder row lists.
//
//   scope "home" : jobs on the calendar from 14 days ago to 7 days ahead,
//                  clock-ins from the last 7 days, sub pay from the last 60
//   scope "all"  : every job, clock-ins from the last `clockDays` days
//
// Reads only. Writes happen in utils/jobReminders.js.

import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  buildClockInRows,
  buildDupWORows,
  buildProposalJobRows,
  buildNoTechRows,
  buildSubPayRows,
  dismissDocId,
  dismissedSet,
  loadClockData,
  loadDismissals,
} from "./jobReminders";
import { loadPeople } from "./subPayPayments";
import { canSeeSubPay } from "./subPayTerms";

const DAY = "YYYY-MM-DD";

/* WO-bearing records that aren't jobs (jobs come from the page). Only the
   few fields the duplicate check needs. */
const loadWORecords = async () => {
  const [inv, prop, add] = await Promise.all([
    getDocs(collection(db, "approvedInvoices")),
    getDocs(collection(db, "proposals")),
    getDocs(collection(db, "addItem")).catch(() => ({ docs: [] })),
  ]);
  const out = [];
  const push = (col, d, extra = {}) => {
    const x = d.data() || {};
    const wo = String(x.wo ?? "").trim();
    if (!wo) return;
    out.push({
      collection: col,
      id: d.id,
      wo,
      siteName: x.siteName || x.propertyName || "",
      scope: x.scope || "",
      status: x.status || "",
      createdKey: String(x.invoiceDate || x.submittedDate || x.createdAt || "").slice(0, 10),
      ...extra,
    });
  };
  inv.docs.forEach((d) => push("approvedInvoices", d));
  prop.docs.forEach((d) => {
    const st = String(d.data()?.status || "");
    if (["Not Approved", "Denied", "Deleted"].includes(st)) return;
    push("proposals", d);
  });
  add.docs.forEach((d) => push("addItem", d));
  return out;
};

export const useJobReminders = ({ jobs = [], users = [], enabled = false, userRole = "", scope = "home", clockDays = 60 }) => {
  const [dismissals, setDismissals] = useState([]);
  const [clock, setClock] = useState({ logs: [], visits: [] });
  const [people, setPeople] = useState(null);
  const [woRecords, setWoRecords] = useState([]); // invoices / proposals / addItem, for duplicate WOs
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  const showSubPay = canSeeSubPay(userRole);
  // Renumbering changes money records, so only Admin / Manager get this list.
  const showDupWO = ["Admin", "Manager"].includes(userRole);
  const today = moment().format(DAY);
  const clockFrom = moment()
    .subtract(scope === "home" ? 6 : Math.max(1, clockDays) - 1, "days")
    .format(DAY);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError("");
    try {
      const [d, c, p, w] = await Promise.all([
        loadDismissals().catch((err) => {
          console.warn("Reminders: dismissals not loaded", err);
          return [];
        }),
        loadClockData(clockFrom, today).catch((err) => {
          console.warn("Reminders: clock-ins not loaded", err);
          return { logs: [], visits: [] };
        }),
        showSubPay ? loadPeople().catch(() => null) : Promise.resolve(null),
        showDupWO ? loadWORecords().catch(() => []) : Promise.resolve([]),
      ]);
      setDismissals(d);
      setClock(c);
      setPeople(p);
      setWoRecords(w);
      setLoaded(true);
    } catch (err) {
      console.error("Reminders load failed:", err);
      setError(err?.message || "Could not load reminders.");
    } finally {
      setLoading(false);
    }
  }, [enabled, clockFrom, today, showSubPay, showDupWO]);

  useEffect(() => {
    load();
  }, [load]);

  const dismissed = useMemo(() => dismissedSet(dismissals), [dismissals]);

  const rows = useMemo(() => {
    if (!enabled || !loaded) return { dupWO: [], proposalJob: [], noTech: [], clockedIn: [], subPay: [] };
    const home = scope === "home";
    const proposalJob = buildProposalJobRows({
      jobs,
      dismissed,
      createdSince: home ? moment().subtract(60, "days").format(DAY) : "",
    });
    // A new proposal job is set up (date + techs together) from its own
    // reminder, so it is not listed a second time under "no tech".
    const inProposal = new Set(proposalJob.map((r) => r.jobId));
    const jobRecords = jobs
      .filter((j) => j.wo !== undefined && j.wo !== null && String(j.wo).trim() !== "")
      .map((j) => ({
        collection: j.__col || "jobs",
        id: j.id,
        wo: String(j.wo),
        siteName: j.siteName || j.propertyName || "",
        scope: j.scope || "",
        status: j.status || "",
        createdKey: String(j.createdAt || j.startDate || "").slice(0, 10),
      }));
    return {
      dupWO: showDupWO ? buildDupWORows({ records: [...jobRecords, ...woRecords], dismissed }) : [],
      proposalJob,
      noTech: buildNoTechRows({
        jobs,
        dismissed,
        from: home ? moment().subtract(14, "days").format(DAY) : "",
        to: home ? moment().add(7, "days").format(DAY) : "",
      }).filter((r) => !inProposal.has(r.jobId)),
      clockedIn: buildClockInRows({ logs: clock.logs, visits: clock.visits, jobs, users, dismissed }),
      subPay: showSubPay
        ? buildSubPayRows({ jobs, people, dismissed, from: home ? moment().subtract(60, "days").format(DAY) : "" })
        : [],
    };
  }, [enabled, loaded, scope, jobs, users, dismissed, clock, people, showSubPay, showDupWO, woRecords]);

  const addDismissal = useCallback((saved) => setDismissals((prev) => [...prev, saved]), []);
  const removeDismissal = useCallback(
    (kind, key) => setDismissals((prev) => prev.filter((d) => dismissDocId(d.kind, d.key) !== dismissDocId(kind, key))),
    []
  );

  return { rows, dismissals, loading, loaded, error, reload: load, addDismissal, removeDismissal, showSubPay, showDupWO };
};
